import React, { useState } from 'react';
import './Contact.css';

function Contact() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);

  const contactMethods = [
    {
      icon: '✉️',
      title: 'Email Us',
      details: ['info@theshapesunited.com', 'support@theshapesunited.com'],
      action: 'mailto:info@theshapesunited.com',
      description: 'For general inquiries and support'
    },
    {
      icon: '📞',
      title: 'Call Us',
      details: ['(02) 1234 5678', 'Mon-Fri: 9am-6pm'],
      action: 'tel:+61212345678',
      description: 'Speak directly with our team'
    },
    {
      icon: '📍',
      title: 'Visit Us',
      details: ['123 Fashion Street', 'Sydney NSW 2000', 'Australia'],
      action: 'https://maps.google.com',
      description: 'Our headquarters location'
    },
    {
      icon: '💬',
      title: 'Live Chat',
      details: ['Available 24/7', 'Quick response'],
      action: '#chat',
      description: 'Instant messaging support'
    }
  ];

  const departments = [
    {
      name: 'Customer Support',
      email: 'support@theshapesunited.com',
      phone: '(02) 1234 5678',
      hours: 'Mon-Fri 9am-6pm AEST'
    },
    {
      name: 'Sales & Wholesale',
      email: 'sales@theshapesunited.com',
      phone: '(02) 1234 5679',
      hours: 'Mon-Fri 9am-5pm AEST'
    },
    {
      name: 'NDIS & HCP Inquiries',
      email: 'ndis@theshapesunited.com',
      phone: '1300 000 000',
      hours: 'Mon-Fri 8am-4pm AEST'
    },
    {
      name: 'Media & Press',
      email: 'media@theshapesunited.com',
      phone: '(02) 1234 5680',
      hours: 'By appointment'
    }
  ];

  const faqs = [
    {
      question: 'What is your response time for emails?',
      answer: 'We typically respond within 24 hours on business days. For urgent matters, please call us directly.'
    },
    {
      question: 'Do you offer virtual consultations?',
      answer: 'Yes! We offer free virtual consultations to help you find the perfect adaptive clothing. Book through our website.'
    },
    {
      question: 'How can I become a stockist?',
      answer: 'Visit our "Become a Stockist" page or email sales@theshapesunited.com with your business details.'
    },
    {
      question: 'Where are your products manufactured?',
      answer: 'Our products are ethically manufactured in Australia and overseas, following fair trade practices.'
    }
  ];

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    // Simulate API call
    await new Promise(resolve => setTimeout(resolve, 1500));
    
    setIsSubmitting(false);
    setSubmitSuccess(true);
    
    // Reset form after success
    setTimeout(() => {
      setFormData({
        name: '',
        email: '',
        phone: '',
        subject: '',
        message: ''
      });
      setSubmitSuccess(false);
    }, 5000);
  };

  return (
    <div className="contact-page">
      {/* Hero Section */}
      <section className="contact-hero">
        <div className="hero-overlay">
          <div className="container">
            <div className="hero-content">
              <h1>Get In Touch</h1>
              <p className="hero-subtitle">We're here to help you find the perfect adaptive clothing solution</p>
              <p className="hero-text">
                Have questions about our products, sizing, NDIS funding, or becoming a stockist? 
                Our team is ready to assist you with personalized support.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Quick Contact Methods */}
      <section className="quick-contact-section">
        <div className="container">
          <div className="section-header">
            <h2>Choose How You'd Like to Connect</h2>
            <p className="section-subtitle">Multiple ways to reach our supportive team</p>
          </div>
          
          <div className="contact-methods-grid">
            {contactMethods.map((method, index) => (
              <a 
                key={index}
                href={method.action}
                className="contact-method-card"
                target={method.action.includes('http') ? '_blank' : '_self'}
                rel={method.action.includes('http') ? 'noopener noreferrer' : ''}
              >
                <div className="method-icon">{method.icon}</div>
                <div className="method-content">
                  <h3>{method.title}</h3>
                  <p className="method-description">{method.description}</p>
                  <div className="method-details">
                    {method.details.map((detail, i) => (
                      <p key={i} className="detail-item">{detail}</p>
                    ))}
                  </div>
                </div>
                <div className="method-arrow">→</div>
              </a>
            ))}
          </div>
        </div>
      </section>

      {/* Main Contact Form */}
      <section className="contact-form-section">
        <div className="container">
          <div className="form-container">
            <div className="form-header">
              <h2>Send Us a Message</h2>
              <p>Fill out the form below and we'll get back to you as soon as possible.</p>
            </div>
            
            {submitSuccess && (
              <div className="success-message">
                <div className="success-icon">✓</div>
                <div className="success-content">
                  <h3>Message Sent Successfully!</h3>
                  <p>Thank you for contacting us. We'll get back to you within 24 hours.</p>
                </div>
              </div>
            )}
            
            <form onSubmit={handleSubmit} className="main-contact-form">
              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="name">Full Name *</label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    placeholder="Your full name"
                    required
                    disabled={isSubmitting}
                  />
                </div>
                
                <div className="form-group">
                  <label htmlFor="email">Email Address *</label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    placeholder="Your email address"
                    required
                    disabled={isSubmitting}
                  />
                </div>
              </div>
              
              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="phone">Phone Number</label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleInputChange}
                    placeholder="Your phone number"
                    disabled={isSubmitting}
                  />
                </div>
                
                <div className="form-group">
                  <label htmlFor="subject">Subject *</label>
                  <select
                    id="subject"
                    name="subject"
                    value={formData.subject}
                    onChange={handleInputChange}
                    required
                    disabled={isSubmitting}
                  >
                    <option value="">Select a subject</option>
                    <option value="product">Product Inquiry</option>
                    <option value="sizing">Sizing & Fit</option>
                    <option value="ndis">NDIS & HCP</option>
                    <option value="wholesale">Wholesale & Stockist</option>
                    <option value="returns">Returns & Exchanges</option>
                    <option value="other">Other</option>
                  </select>
                </div>
              </div>
              
              <div className="form-group">
                <label htmlFor="message">Your Message *</label>
                <textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleInputChange}
                  placeholder="Tell us how we can help you..."
                  rows="6"
                  required
                  disabled={isSubmitting}
                ></textarea>
              </div>
              
              <div className="form-footer">
                <button 
                  type="submit" 
                  className="submit-button"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? (
                    <>
                      <span className="spinner"></span>
                      Sending...
                    </>
                  ) : (
                    'Send Message'
                  )}
                </button>
                <p className="form-note">
                  We respect your privacy. Your information will never be shared with third parties.
                </p>
              </div>
            </form>
          </div>
        </div>
      </section>

      {/* Departments & Team */}
      <section className="departments-section">
        <div className="container">
          <div className="section-header">
            <h2>Contact the Right Team</h2>
            <p className="section-subtitle">Connect with the department that can best assist you</p>
          </div>
          
          <div className="departments-grid">
            {departments.map((dept, index) => (
              <div key={index} className="department-card">
                <div className="department-header">
                  <h3>{dept.name}</h3>
                  <div className="dept-badge">{dept.hours}</div>
                </div>
                
                <div className="department-contacts">
                  <a href={`mailto:${dept.email}`} className="contact-link email">
                    <span className="link-icon">✉️</span>
                    <span className="link-text">{dept.email}</span>
                  </a>
                  
                  <a href={`tel:${dept.phone.replace(/\D/g, '')}`} className="contact-link phone">
                    <span className="link-icon">📞</span>
                    <span className="link-text">{dept.phone}</span>
                  </a>
                </div>
                
                <div className="department-actions">
                  <a href={`mailto:${dept.email}`} className="action-btn">
                    Send Email
                  </a>
                  <a href={`tel:${dept.phone.replace(/\D/g, '')}`} className="action-btn outline">
                    Call Now
                  </a>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="contact-faq">
        <div className="container">
          <div className="section-header">
            <h2>Frequently Asked Questions</h2>
            <p className="section-subtitle">Quick answers to common questions</p>
          </div>
          
          <div className="faq-grid">
            {faqs.map((faq, index) => (
              <div key={index} className="faq-item">
                <div className="faq-question">
                  <h3>{faq.question}</h3>
                  <span className="faq-icon">+</span>
                </div>
                <div className="faq-answer">
                  <p>{faq.answer}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Emergency Contact */}
      <section className="emergency-section">
        <div className="container">
          <div className="emergency-card">
            <div className="emergency-icon">⚠️</div>
            <div className="emergency-content">
              <h3>Need Urgent Assistance?</h3>
              <p>For urgent order issues or immediate support, call our emergency line:</p>
              <a href="tel:1300000000" className="emergency-phone">
                <span className="phone-icon">📞</span>
                <span className="phone-number">1300 000 000</span>
              </a>
              <p className="emergency-note">Available 24/7 for urgent matters only</p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}

export default Contact;